<template>
  <teleport to="#modals">
    <vue-resizable
      data-cy="communication-tool-draggable"
      class="z-101"
      drag-selector=".draggable"
      :width="quickActionsOptions.width"
      :height="quickActionsOptions.height"
      :top="quickActionsOptions.top"
      :left="quickActionsOptions.left"
      :active="[]"
      @drag:end="updatePosition"
    >
      <div
        ref="draggable"
        class="draggable flex items-center space-x-1 rounded-full border border-disabled/10 bg-grey-navigation px-2 text-white shadow-md"
      >
        <div
          class="border-r-white/10 border-r h-full flex items-center justify-center py-4 pr-2"
        >
          <icon-base
            :icon="IconCardView"
            view-box="0 0 24 24"
            class="flex-none rotate-90 transform cursor-grab"
          />
        </div>
        <div class="flex items-center gap-x-1">
          <div
            v-for="({ component, listeners }, name) in components"
            :key="name"
            class="h-8 w-8 rounded-full flex items-center justify-center hover:bg-white/10 cursor-pointer"
          >
            <component :is="component" :data-cy="name" v-on="listeners ?? {}" />
          </div>
        </div>
        <div
          class="border-l-white/10 border-l h-full flex items-center justify-center pl-1"
        >
          <div
            class="h-8 w-8 rounded-full flex items-center justify-center hover:bg-white/10 cursor-pointer"
          >
            <icon-base
              :icon="IconClose"
              view-box="0 0 18 18"
              data-cy="close-quick-actions"
              class="cursor-pointer text-white/10 hover:text-white"
              @click="isVisible = false"
            />
          </div>
        </div>
      </div>
    </vue-resizable>
  </teleport>
  <standalone-call
    v-if="canShowCallForm && !shouldDisableTwilioCall"
    @close="canShowCallForm = false"
    @call:start="handleCall"
  />
  <teleport v-if="callDialog" to="#modals">
    <communication-log-call-dialog
      class="fixed z-50 shadow-lg"
      :value="callDialog"
      @close-dialog="callDialog = null"
    />
  </teleport>
  <send-deal-communication-modal
    v-if="canShowActivityModal"
    :type="modalType"
    standalone
    anchor-bottom-left
    @close-dialog="canShowActivityModal = false"
  />
</template>

<script setup lang="ts">
import { ref } from "vue";
import VueResizable from "vue-resizable";

import { storeToRefs } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import {
  CallErrorReason,
  CommunicationToolItemType,
  CommunicationType,
} from "@/enums/communicationLogs";
import { CommunicationProvider } from "@/enums/communicationProviders";
import { setActiveCallSession } from "@/helpers/communication";
import { DEFAULT_DIALER } from "@/helpers/constants";
import { DEFAULT_QUICK_ACTIONS_OPTIONS } from "@/helpers/constants/deals";
import { TWILIO_PIP_OPEN } from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";
import { useTwilio } from "@/hooks/communicationProviders";
import { useContacts } from "@/hooks/contacts";
import { useLocalStorageSetting } from "@/hooks/options";
import { useResizable } from "@/hooks/resizable";
import { useAnalyticsStore } from "@/stores/analytics";
import { useCallsStore } from "@/stores/calls";
import useQuickActionsStore from "@/stores/quickActions";

import SendDealCommunicationModal from "@/components/communication/SendDealCommunicationModal.vue";
import Call from "@/components/communication/communicationTypes/Call.vue";
import Sms from "@/components/communication/communicationTypes/Sms.vue";
import StandaloneCall from "@/components/communication/standalone/StandaloneCall.vue";
import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";
import IconCardView from "@/components/icons/IconCardView.vue";
import IconClose from "@/components/icons/IconClose.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type {
  CallLog,
  CommunicationModalType,
} from "@/models/communicationLogs";
import type { Component } from "vue";

interface CommunicationToolItemOptions {
  component: Component;
  isCommunicationItem?: boolean;
  listeners?: Record<
    string,
    (event?: MouseEvent, type?: CommunicationModalType) => void
  >;
}

const { trackEvent } = useAnalyticsStore();
const quickActionsStore = useQuickActionsStore();
const callsStore = useCallsStore();
const { preferredDialer } = useTwilio();
const { callClientStandalone } = useContacts();
const quickActionsOptions = useLocalStorageSetting(
  "quickActionsOptions",
  DEFAULT_QUICK_ACTIONS_OPTIONS
);
const emitter = useEmitter();

const { isVisible } = storeToRefs(quickActionsStore);
const { activeCallSession, isPiPOpen, shouldDisableTwilioCall } =
  storeToRefs(callsStore);

const callDialog = ref<CallLog | null>(null);
const canShowCallForm = ref(false);
const canShowActivityModal = ref(false);

const modalType = ref<CommunicationModalType>(CommunicationType.email);
const draggable = ref<HTMLElement | null>(null);

const { updatePosition, resizeListener } = useResizable(
  quickActionsOptions,
  draggable
);

const handleCall = async (phoneNumber: string) => {
  if (shouldDisableTwilioCall.value) {
    return;
  }
  const result = await callClientStandalone({
    to: phoneNumber,
    provider: preferredDialer.value ?? DEFAULT_DIALER,
  });
  if (!result?.connectionLog?.id) {
    trackEvent(AnalyticsEvent.CallOutError, {
      reason: CallErrorReason.StandaloneCallMissingApiResponse,
    });
    return;
  }
  canShowCallForm.value = false;
  setActiveCallSession(result);

  // Auto-open PiP for Twilio calls
  if (
    result.callOutPayload?.provider === CommunicationProvider.Twilio &&
    !isPiPOpen.value
  ) {
    emitter.emit(TWILIO_PIP_OPEN);
  }
  callDialog.value = result.connectionLog;
};

const showCallForm = () => {
  if (activeCallSession.value) {
    return;
  }
  canShowActivityModal.value = false;
  canShowCallForm.value = true;
};

const showCommunicationModal = (
  e?: MouseEvent,
  type?: CommunicationModalType
) => {
  if (!e?.target || !type || canShowActivityModal.value) {
    return;
  }
  canShowActivityModalType(type);
};

const canShowActivityModalType = (type: CommunicationModalType) => {
  canShowCallForm.value = false;
  canShowActivityModal.value = true;
  modalType.value = type;
  resizeListener();
};

const components: Record<
  CommunicationToolItemType.call | CommunicationToolItemType.sms,
  CommunicationToolItemOptions
> = {
  [CommunicationToolItemType.call]: {
    component: Call,
    isCommunicationItem: false,
    listeners: {
      "client:call": showCallForm,
    },
  },
  [CommunicationToolItemType.sms]: {
    component: Sms,
    isCommunicationItem: true,
    listeners: {
      "modal:show": showCommunicationModal,
    },
  },
};
</script>
